import moment from 'moment-timezone'
import { GRID_TIMESTAMP, OFFSET_HOURS_TIMESTAMP, ONE_DAY_TIMESTAMP } from '../constants'
import { MappingChartDataPoint } from '@/components/patient-stats/graphs/mapping-chart-options/types'
import { DataPoint } from '@/jsonapi/CommonTypes'
import { round } from 'lodash-es'

export function convertToMappingChartDataPoint<T extends DataPoint>(
  timezone,
  gridTimestamp?: number
): (dataPoint: T) => MappingChartDataPoint<T> {
  return (dataPoint: T): MappingChartDataPoint<T> => {
    const rawStartTimestamp = dataPoint.timestamp
    const rawDuration = dataPoint.value || 0
    const rawTimestampStartOfDay = moment(rawStartTimestamp)
      .tz(timezone)
      .startOf('day')
      .valueOf()
    const rawEndTimestamp = rawStartTimestamp + rawDuration
    const shiftedStartTimestamp = rawStartTimestamp + OFFSET_HOURS_TIMESTAMP
    const shiftedEndTimestamp = rawEndTimestamp + OFFSET_HOURS_TIMESTAMP
    const shiftedTimestampStartOfDay = moment(shiftedStartTimestamp)
      .tz(timezone)
      .startOf('day')
      .valueOf()
    const shiftedStartTimeFromStartOfDay = shiftedStartTimestamp - shiftedTimestampStartOfDay
    const shiftedEndTimeFromStartOfDay = shiftedEndTimestamp - shiftedTimestampStartOfDay
    const gridIndex = gridTimestamp ? round(shiftedStartTimeFromStartOfDay / gridTimestamp) : null

    return {
      rawStartTimestamp,
      rawDuration,
      rawTimestampStartOfDay,
      rawEndTimestamp,
      shiftedStartTimestamp,
      shiftedEndTimestamp,
      shiftedTimestampStartOfDay,
      shiftedStartTimeFromStartOfDay,
      shiftedEndTimeFromStartOfDay,
      rawData: dataPoint,
      gridIndex,
    }
  }
}

export function divideDataSpreadingAcrossOverTwoDays<T>(dataPoints: Array<MappingChartDataPoint<T>>, timezone: string) {
  return dataPoints
    .map(dataPoint => {
      if (
        moment(dataPoint.shiftedEndTimestamp)
          .tz(timezone)
          .startOf('day')
          .valueOf() !== dataPoint.shiftedTimestampStartOfDay
      ) {
        const nextStartOfDay = moment(dataPoint.shiftedTimestampStartOfDay)
          .add(1, 'd')
          .valueOf()
        return [
          {
            // original day
            ...dataPoint,
            shiftedEndTimestamp: nextStartOfDay,
            shiftedEndTimeFromStartOfDay: ONE_DAY_TIMESTAMP,
          },
          {
            // next day
            ...dataPoint,
            shiftedStartTimestamp: nextStartOfDay,
            shiftedTimestampStartOfDay: nextStartOfDay,
            shiftedStartTimeFromStartOfDay: 0,
            shiftedEndTimeFromStartOfDay: dataPoint.shiftedEndTimeFromStartOfDay - ONE_DAY_TIMESTAMP,
          },
        ]
      } else {
        return dataPoint
      }
    })
    .flat(Infinity)
}

export function groupByDayForMCDataPoint<T>(
  dataPoints: Array<MappingChartDataPoint<T>>
): { [key: string]: Array<MappingChartDataPoint<T>> } {
  const dataPointsMapByDay = {}
  dataPoints.forEach(dataPoint => {
    const { shiftedTimestampStartOfDay } = dataPoint
    if (!dataPointsMapByDay[shiftedTimestampStartOfDay]) {
      dataPointsMapByDay[shiftedTimestampStartOfDay] = []
    }
    dataPointsMapByDay[shiftedTimestampStartOfDay].push(dataPoint)
  })
  return dataPointsMapByDay
}

export function buildSeriesDataForSinglePoint<T>(dataPointsMapByDay: {
  [key: string]: Array<MappingChartDataPoint<T>>
}): Array<{ value: [number, string, MappingChartDataPoint<T>] }> {
  return Object.entries(dataPointsMapByDay)
    .map(([key, values]) => {
      return values.map(value => {
        return {
          value: [value.gridIndex, key, value],
        }
      })
    })
    .flat(Infinity)
}

export function buildSeriesDataForMultiPoint<T>(
  dataPointsMapByDay: { [key: string]: Array<MappingChartDataPoint<T>> },
  yAxis: number[]
): Array<{ value: [number, number, number, MappingChartDataPoint<T>] }> {
  return Object.entries(dataPointsMapByDay)
    .map(([key, values]) => {
      return values.map(value => {
        return {
          value: [
            yAxis.findIndex(y => y === value.shiftedTimestampStartOfDay),
            round(value.shiftedStartTimeFromStartOfDay / GRID_TIMESTAMP),
            round(value.shiftedEndTimeFromStartOfDay / GRID_TIMESTAMP),
            value,
          ],
        }
      })
    })
    .flat(Infinity)
}
